import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { authMe, mfaVerify, mfaResend, mfaStatus, logout } from "@/lib/afmApi";

const TOKEN_KEY = "sona_session_token";
const errText = (e) => e?.response?.data?.detail || "No se pudo verificar el código";

// Verificación en dos pasos: el código de 6 dígitos que llega al correo.
export default function Verificar() {
  const { signInWith } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(() => { try { return sessionStorage.getItem("afm_mfa_hint") || ""; } catch (e) { return ""; } });
  const [wait, setWait] = useState(60);
  const inputRef = useRef(null);

  useEffect(() => {
    let token = "";
    try { token = localStorage.getItem(TOKEN_KEY) || ""; } catch (e) { /* noop */ }
    if (!token) { navigate("/login", { replace: true }); return; }
    mfaStatus().then((s) => {
      if (!s.mfa_pending) { finish(token); return; }
      if (s.mfa_email_hint) setHint(s.mfa_email_hint);
    }).catch(() => navigate("/login", { replace: true }));
    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const finish = async (token) => {
    const user = await authMe(token);
    try { sessionStorage.removeItem("afm_mfa_hint"); } catch (e) { /* noop */ }
    signInWith({ ...user, session_token: token });
    toast.success("Bienvenido de vuelta");
    navigate("/dashboard", { replace: true });
  };

  const submit = async (e) => {
    e?.preventDefault();
    const clean = code.replace(/\D/g, "");
    if (clean.length !== 6) { toast.error("El código tiene 6 dígitos"); return; }
    setBusy(true);
    try {
      await mfaVerify(clean, remember);
      await finish(localStorage.getItem(TOKEN_KEY));
    } catch (err) {
      const st = err?.response?.status;
      toast.error(errText(err));
      if (st === 401 || st === 429) { cancel(); return; }
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try { const r = await mfaResend(); if (r.mfa_email_hint) setHint(r.mfa_email_hint); toast.success("Te enviamos un código nuevo"); setWait(60); }
    catch (err) { toast.error(errText(err)); }
  };

  const cancel = async () => {
    await logout();
    try { localStorage.removeItem(TOKEN_KEY); sessionStorage.removeItem("afm_mfa_hint"); } catch (e) { /* noop */ }
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-6" data-testid="mfa-page">
      <form onSubmit={submit} className="w-full max-w-sm text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-black"><ShieldCheck className="h-7 w-7" /></div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">Always Fre$h Music</div>
        <h1 className="font-serif text-3xl font-bold tracking-tight text-black">Revisa tu correo</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Te enviamos un código de 6 dígitos{hint ? <> a <strong className="text-black">{hint}</strong></> : ""}. Vence en 10 minutos.
        </p>
        <input
          ref={inputRef} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric" autoComplete="one-time-code" placeholder="000000" aria-label="Código de 6 dígitos"
          className="mt-6 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-center font-mono text-2xl tracking-[0.5em] text-black outline-none focus:border-black"
          data-testid="mfa-code"
        />
        <label className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-black" />
          Recordar este navegador
        </label>
        <button type="submit" disabled={busy || code.length !== 6} className="mt-5 w-full rounded-xl bg-black px-4 py-3 text-sm font-semibold text-white disabled:opacity-40" data-testid="mfa-submit">
          {busy ? "Verificando…" : "Entrar"}
        </button>
        <div className="mt-5 flex items-center justify-between text-sm">
          <button type="button" onClick={resend} disabled={wait > 0} className="font-semibold text-slate-600 hover:text-black disabled:text-slate-400" data-testid="mfa-resend">
            {wait > 0 ? `Reenviar código (${wait}s)` : "Reenviar código"}
          </button>
          <button type="button" onClick={cancel} className="text-slate-500 hover:text-black">Usar otra cuenta</button>
        </div>
        <p className="mt-6 text-[11px] leading-relaxed text-slate-400">Nunca compartas este código. El equipo de Always Fresh nunca te lo va a pedir.</p>
      </form>
    </div>
  );
}
